import { useState } from 'react'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { Avatar } from '@/components/ui/Avatar'
import { ProfileEditDialog } from '@/components/ui/ProfileEditDialog'
import { SettingsDialog } from '@/components/ui/SettingsDialog'
import { useAuthStore } from '@/lib/store/auth-store'
import { getClient } from '@/lib/matrix/client'
import { useUIStore } from '@/lib/store/ui-store'
import { useConnectionStore } from '@/lib/store/connection-store'

interface HeaderProps {
  onSearchOpen?: () => void
  onKeyRestore?: () => void
  onSecurity?: () => void
  onCodexAuth?: () => void
}

export function Header({ onSearchOpen, onKeyRestore, onSecurity, onCodexAuth }: HeaderProps) {
  const [showProfileEdit, setShowProfileEdit] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const { userId, logout } = useAuthStore()
  const { sidebarOpen, setSidebarOpen } = useUIStore()
  const syncState = useConnectionStore((s) => s.syncState)

  const syncPillClass =
    syncState === 'connected' ? 'sync-pill'
      : syncState === 'reconnecting' ? 'sync-pill sync-pill-warn animate-pulse-subtle'
      : 'sync-pill sync-pill-crit'
  const syncLabel =
    syncState === 'connected' ? 'Connected' : syncState === 'reconnecting' ? 'Reconnecting' : 'Offline'

  return (
    <header className="topbar">
      <div className="flex items-center gap-2.5">
        {/* Mobile-only sidebar toggle. Wrapped in lg:hidden because `.icon-btn`
            (loaded after Tailwind) would otherwise override `lg:hidden` on the button. */}
        <span className="lg:hidden">
          <button className="icon-btn" onClick={() => setSidebarOpen(!sidebarOpen)} title="Menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </span>
        <span className="logo-mark">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </span>
        <span className="brand">ChatPulse</span>
        <span className={syncPillClass}>
          <span className="sync-dot" />
          {syncLabel}
        </span>
      </div>

      <div className="flex items-center gap-0.5">
        {onSearchOpen && (
          <button onClick={onSearchOpen} className="icon-btn" title="Search messages">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
        )}
        {onCodexAuth && (
          <button onClick={onCodexAuth} className="icon-btn" title="Connect AI (Codex)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z" />
              <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
              <circle cx="12" cy="11" r="1" />
            </svg>
          </button>
        )}
        {onSecurity && (
          <button onClick={onSecurity} className="icon-btn" title="Sessions & security">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </button>
        )}
        {onKeyRestore && (
          <button onClick={onKeyRestore} className="icon-btn" title="Restore encryption keys">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
            </svg>
          </button>
        )}
        <ThemeToggle />
        <button onClick={() => setShowSettings(true)} className="icon-btn" title="Settings">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
        {userId && (
          <button onClick={() => logout()} className="icon-btn" title="Logout">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        )}
        {userId && (
          <button onClick={() => setShowProfileEdit(true)} className="cursor-pointer ml-1.5 rounded-full ring-2 ring-[rgb(var(--np-accent))]" title="Edit profile">
            <Avatar mxc={getClient()?.getUser(userId)?.avatarUrl} name={userId} userId={userId} size={32} />
          </button>
        )}
      </div>
      {showProfileEdit && <ProfileEditDialog onClose={() => setShowProfileEdit(false)} />}
      {showSettings && <SettingsDialog onClose={() => setShowSettings(false)} />}
    </header>
  )
}
